import { Divider, ListItemText } from "~/common/mui";
import type React from "react";
import { forwardRef, Fragment } from "react";
import { RadiusSelect } from "~/features/salesPartner/components/SearchCard/GooglePlacesInput/RadiusSelect";
import { styled } from "@mui/system";
import { SearchType } from "~/domains/salesPartner/api/getSalesPartnerSearch";
import { useTranslation } from "react-i18next";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import classes from "./GroupedSelect.module.less";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { C_ON_SECONDARY_CONTAINER, C_ON_SURFACE_LOW_EMPHASIS, C_SURFACE } from "~/common/components/icon/Colors";
import { LinkButton } from "~/common/components/Link";
import { SearchInputGroup } from "~/common/components/SearchInputGroup";

export interface GroupedSelectRef {
  reset: () => void;
}

export interface Suggestion {
  label: string;
  group: string;
  placeID?: string;
  searchType?: SearchType;
  searchQuery?: string;
}

interface GroupedSelectProps {
  disabled?: boolean;
  openFilterDialog: () => void;
  showFilterButton: boolean;
  selectedOption: Suggestion | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<Suggestion | null>>;
  suggestedCombinedList: Suggestion[];
  onSearchInputChange: (input: string) => void;
  radiusValue?: number;
  onRadiusChange?: (value: number) => void;
}

const GroupHeader = styled("div")({
  position: "sticky",
  padding: "8px",
  paddingTop: "16px",
  color: C_ON_SURFACE_LOW_EMPHASIS,
  backgroundColor: C_SURFACE,
  textTransform: "capitalize",
  ...getFontStyle(FontSize.BodySmall),
});

const GroupItems = styled("ul")({
  padding: "0px",
  backgroundColor: C_SURFACE,
  ...getFontStyle(FontSize.BodyLarge),
});

const GroupedSelect = forwardRef<GroupedSelectRef, GroupedSelectProps>((props, ref) => {
  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  const {
    disabled,
    openFilterDialog,
    showFilterButton,
    selectedOption,
    suggestedCombinedList,
    onSearchInputChange,
    setSelectedOption,
    radiusValue,
    onRadiusChange,
  } = props;

  const { t } = useTranslation();

  const autocompleteCombinedInput = (clickable: boolean) => (
    <div className={classes.groupedSelectContainer}>
      <SearchInputGroup.Autocomplete
        disabled={disabled}
        fullWidth
        value={selectedOption}
        filterOptions={(x) => x} // skip MUI's default filter
        options={suggestedCombinedList}
        open={clickable ? false : undefined}
        popupIcon={clickable ? null : undefined}
        groupBy={(option) => (typeof option === "string" ? "" : option.group)}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        renderOption={(props, option, { index }) => (
          <Fragment key={option.label}>
            <li {...props}>
              <ListItemText primary={option.label} />
            </li>
            <Divider />
          </Fragment>
        )}
        placeholder={t("partnerFinder.search.placeholder")}
        onInputChange={(e, newValue) => {
          onSearchInputChange(newValue);
        }}
        onChange={(event, newValue) => {
          setSelectedOption(newValue);
        }}
      />
      {!isMobile && (
        <>
          <SearchInputGroup.Divider />
          <RadiusSelect
            disabled={typeof selectedOption !== "string" && selectedOption?.group == SearchType.Partner}
            value={radiusValue ?? 100}
            onChange={onRadiusChange ?? (() => {})}
          />
        </>
      )}
    </div>
  );

  const searchClickableButton = (
    <LinkButton onClick={openFilterDialog} className={classes.linkButton}>
      {isMobile && autocompleteCombinedInput(true)}
      <div className={classes.filterIconContainer}>
        <FilterAltOutlinedIcon
          fontSize="large"
          sx={{
            color: C_ON_SECONDARY_CONTAINER,
          }}
        />
      </div>
    </LinkButton>
  );

  return (
    <div className={classes.groupedSelectContainerWithFilterIcon}>
      {autocompleteCombinedInput(false)}
      {showFilterButton && isMobile && searchClickableButton}
    </div>
  );
});

export default GroupedSelect;
